import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Zap, Mail, Users, Shield, BookOpen, Clock, Star } from 'lucide-react';
import { keywordPages } from '../data/keywordPages';
import FooterTrust from '../components/FooterTrust';
import NotFound from '../components/NotFound';

const BOT_LINK = "https://t.me/instant_email_bot";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/dbwmuy0k_A9358B2D-0233-4058-88E9-3B869B36D815.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

function trackCTA(action) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, { event_category: 'Keyword_CTA' });
  }
}

function TelegramIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  );
}

export default function KeywordPage({ slug: propSlug }) {
  const location = useLocation();
  const slug = propSlug || location.pathname.replace(/^\//, '');
  const page = keywordPages.find(p => p.slug === slug);

  if (!page) return <NotFound />;

  const relatedKw = keywordPages.filter(p => page.relatedPages.includes(p.slug));

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <SEOMeta page={page} />

      {/* Header */}
      <header className="bg-white border-b border-gray-200/60 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="kw-logo-link">
            <img src={LOGO_URL} alt="TelegramEmailBot" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-outfit font-bold text-lg text-gray-900">TelegramEmailBot</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 font-manrope text-sm text-gray-600">
            <Link to="/use-cases" className="hover:text-[#0088CC] transition-colors">Use Cases</Link>
            <Link to="/blog" className="hover:text-[#0088CC] transition-colors">Blog</Link>
          </nav>
          <a
            href={BOT_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTA(`kw_header_${page.slug}`)}
            className="font-manrope text-sm font-semibold bg-[#0088CC] text-white px-5 py-2 rounded-full hover:bg-[#006699] transition-all shadow-md"
            data-testid="kw-header-cta"
          >
            Get Started ($4.99)
          </a>
        </div>
      </header>

      {/* Hero - Full Width Gradient Background */}
      <section className="relative pt-16 pb-20 sm:pt-24 sm:pb-28 px-4 overflow-hidden" data-testid="kw-hero">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#f0f9ff] via-white to-[#f0fdf4]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(0,136,204,0.05),transparent_70%)]" />

        <div className="max-w-4xl mx-auto relative">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            {/* Breadcrumb */}
            <motion.nav variants={fadeUp} className="flex items-center gap-2 text-sm font-manrope text-gray-400 mb-8" data-testid="kw-breadcrumb">
              <Link to="/" className="hover:text-[#0088CC] transition-colors">Home</Link>
              <span>/</span>
              <span className="text-gray-600">{page.h1}</span>
            </motion.nav>

            {/* Reading time badge */}
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white border border-gray-200 shadow-sm text-gray-600 font-manrope text-xs font-medium px-3 py-1.5 rounded-full mb-6">
              <BookOpen className="w-3.5 h-3.5" />
              <span>Complete Guide</span>
              <span className="w-1 h-1 bg-gray-300 rounded-full" />
              <Clock className="w-3.5 h-3.5" />
              <span>8 min read</span>
            </motion.div>

            <motion.h1 variants={fadeUp} className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-gray-900 leading-[1.08] mb-6">
              {page.h1}
            </motion.h1>
            <motion.p variants={fadeUp} className="font-manrope text-lg sm:text-xl text-gray-500 max-w-3xl mb-10 leading-relaxed">
              {page.subtitle}
            </motion.p>

            {/* CTA Row */}
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4">
              <a
                href={BOT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCTA(`kw_hero_${page.slug}`)}
                className="inline-flex items-center gap-2.5 bg-[#0088CC] text-white font-manrope font-bold px-8 py-4 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg shadow-[#0088CC]/20"
                data-testid="kw-hero-cta"
              >
                <TelegramIcon className="w-5 h-5" /> Get Started ($4.99)
              </a>
              <div className="flex items-center gap-5 text-sm text-gray-500 font-manrope">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> No signup</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Ready in 30s</span>
                <span className="flex items-center gap-1.5" data-testid="kw-hero-guarantee"><Shield className="w-4 h-4 text-emerald-500" /> 7-day money-back</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="border-b border-gray-100 bg-white sticky top-16 z-40" data-testid="kw-toc">
        <div className="max-w-4xl mx-auto px-4 py-3 overflow-x-auto">
          <div className="flex items-center gap-6 text-sm font-manrope text-gray-500 whitespace-nowrap">
            <span className="text-gray-400 font-semibold text-xs uppercase tracking-wider">Jump to:</span>
            <a href="#content-top" className="hover:text-[#0088CC] transition-colors">Overview</a>
            <a href="#how-it-works" className="hover:text-[#0088CC] transition-colors">How It Works</a>
            <a href="#features" className="hover:text-[#0088CC] transition-colors">Features</a>
            <a href="#pricing-section" className="hover:text-[#0088CC] transition-colors">Pricing</a>
            <a href="#faq-section" className="hover:text-[#0088CC] transition-colors">FAQ</a>
          </div>
        </div>
      </section>

      {/* Main Content - Beautiful Typography */}
      <section id="content-top" className="py-16 sm:py-20 px-4" data-testid="kw-content">
        <div className="max-w-3xl mx-auto">
          <div
            className="keyword-content font-manrope text-[17px] leading-[1.8] text-gray-700"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>
      </section>

      {/* Mid-article CTA Banner */}
      <section className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-[#0088CC] to-[#006699] rounded-2xl p-8 sm:p-10 text-center shadow-xl shadow-[#0088CC]/10">
            <h3 className="font-outfit font-bold text-2xl text-white mb-3">Ready to Try It?</h3>
            <p className="font-manrope text-white/80 mb-6">Get your email address in 30 seconds. One-time $4.99 · 7-day money-back guarantee.</p>
            <a
              href={BOT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTA(`kw_mid_${page.slug}`)}
              className="inline-flex items-center gap-2.5 bg-white text-[#0088CC] font-manrope font-bold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-all hover:-translate-y-0.5 shadow-lg"
              data-testid="kw-mid-cta"
            >
              <TelegramIcon className="w-5 h-5" /> Open @instant_email_bot
            </a>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section id="pricing-section" className="py-16 sm:py-20 px-4 bg-gray-900" data-testid="kw-pricing-cta">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 bg-[#0088CC]/20 text-[#5FB8FF] font-manrope font-semibold text-xs uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              <Star className="w-3.5 h-3.5" /> Simple Pricing
            </div>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-3">One-Time $4.99. Yours Forever.</h2>
            <p className="font-manrope text-gray-400 mb-2">Starter — permanent inbox</p>
            <p className="font-outfit font-black text-5xl sm:text-6xl text-white mb-1">$4.99<span className="text-2xl text-gray-500 font-normal"> one-time</span></p>
            <p className="font-manrope text-sm text-gray-500 mb-2">20 complimentary emails included · 7-day money-back guarantee</p>
            <p className="font-manrope text-xs text-gray-500 mb-8">Need unlimited emails + AI summaries? Upgrade to <span className="text-white font-semibold">Inbox Storage &amp; AI — $14.99/year</span></p>
            <a
              href={BOT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTA(`kw_pricing_${page.slug}`)}
              className="inline-flex items-center gap-2.5 bg-[#0088CC] text-white font-manrope font-bold px-10 py-4 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg text-base"
              data-testid="kw-pricing-cta-btn"
            >
              <TelegramIcon className="w-5 h-5" /> Get Started for $4.99
            </a>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 text-sm text-gray-500 font-manrope">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Permanent inbox</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Team groups</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> AI summaries (Yearly)</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-500" /> Privacy-first</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Guides */}
      <section id="faq-section" className="py-16 sm:py-20 px-4 bg-[#F8F9FA]" data-testid="kw-internal-links">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-outfit font-bold text-2xl text-gray-900 text-center mb-4">Related Guides</h2>
          <p className="font-manrope text-gray-500 text-center mb-10">Explore more email-to-Telegram solutions</p>
          <div className="grid sm:grid-cols-3 gap-5">
            {relatedKw.map((r, i) => (
              <Link key={i} to={`/${r.slug}`} className="block bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg hover:border-[#0088CC]/30 hover:-translate-y-1 transition-all group" data-testid={`kw-related-${i}`}>
                <div className="w-10 h-10 bg-[#0088CC]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0088CC]/20 transition-colors">
                  <Mail className="w-5 h-5 text-[#0088CC]" />
                </div>
                <h3 className="font-outfit font-bold text-base text-gray-900 group-hover:text-[#0088CC] transition-colors mb-2">{r.h1}</h3>
                <p className="font-manrope text-sm text-gray-500 line-clamp-2 mb-3">{r.subtitle}</p>
                <span className="inline-flex items-center gap-1 font-manrope text-xs font-semibold text-[#0088CC]">
                  Read guide <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/use-cases" className="inline-flex items-center gap-2 text-[#0088CC] font-manrope font-semibold hover:underline">
              Browse all 30+ use cases <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-gray-300 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TelegramEmailBot" className="w-5 h-5 rounded object-cover" />
              <span className="font-outfit font-bold text-white text-sm">TelegramEmailBot</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-manrope text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/use-cases" className="hover:text-white transition-colors">Use Cases</Link>
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <span className="font-manrope text-xs text-gray-300">&copy; 2026 TelegramEmailBot</span>
          </div>
          <FooterTrust variant="dark" />
        </div>
      </footer>

      {/* FAQ Schema */}
      <FAQSchema page={page} />

      {/* Content Styling */}
      <style>{`
        .keyword-content h2 {
          font-family: 'Outfit', sans-serif;
          font-weight: 800;
          font-size: 1.75rem;
          color: #111827;
          margin-top: 3.5rem;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f3f4f6;
        }
        .keyword-content h3 {
          font-family: 'Outfit', sans-serif;
          font-weight: 700;
          font-size: 1.25rem;
          color: #1f2937;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .keyword-content p {
          margin-bottom: 1.25rem;
          color: #374151;
        }
        .keyword-content strong {
          color: #111827;
          font-weight: 700;
        }
        .keyword-content code {
          background: #f0f9ff;
          color: #0088CC;
          padding: 0.15rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.9em;
          font-weight: 500;
        }
        .keyword-content ul, .keyword-content ol {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        .keyword-content ul {
          list-style: none;
        }
        .keyword-content ul li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
          color: #374151;
        }
        .keyword-content ul li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.6rem;
          width: 6px;
          height: 6px;
          background: #0088CC;
          border-radius: 50%;
        }
        .keyword-content ol {
          list-style: none;
          counter-reset: step-counter;
        }
        .keyword-content ol li {
          position: relative;
          padding-left: 2.5rem;
          margin-bottom: 0.75rem;
          color: #374151;
          counter-increment: step-counter;
        }
        .keyword-content ol li::before {
          content: counter(step-counter);
          position: absolute;
          left: 0;
          top: 0;
          width: 1.75rem;
          height: 1.75rem;
          background: #f0f9ff;
          color: #0088CC;
          font-weight: 700;
          font-size: 0.8rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid #e0f2fe;
        }
        .keyword-content table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          margin: 2rem 0;
          border-radius: 1rem;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          font-size: 0.875rem;
        }
        .keyword-content thead {
          background: #f8fafc;
        }
        .keyword-content th {
          padding: 0.875rem 1rem;
          text-align: left;
          font-weight: 700;
          color: #1f2937;
          border-bottom: 1px solid #e5e7eb;
        }
        .keyword-content td {
          padding: 0.875rem 1rem;
          border-bottom: 1px solid #f3f4f6;
          color: #4b5563;
        }
        .keyword-content tbody tr:last-child td {
          border-bottom: none;
        }
        .keyword-content tbody tr:hover {
          background: #f9fafb;
        }
        .keyword-content em {
          color: #6b7280;
          font-style: italic;
        }
      `}</style>
    </div>
  );
}

function SEOMeta({ page }) {
  React.useEffect(() => {
    document.title = page.metaTitle;
    const updateMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    updateMeta('description', page.metaDescription);
    updateMeta('keywords', page.keywords.join(', '));
    updateMeta('og:title', page.metaTitle, 'property');
    updateMeta('og:description', page.metaDescription, 'property');
    updateMeta('og:url', `https://telegramemailbot.com/${page.slug}`, 'property');
    const link = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
    link.href = `https://telegramemailbot.com/${page.slug}`;
  }, [page]);
  return null;
}

function FAQSchema({ page }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": page.faqs.map(f => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}
